// BP-83 as one import: every displayed date wears the TENANT's format.
//
// The formatter is still `formatAuditTimestamp` — this file does not add a
// second one. It carries the rules BP-83 says the bare call does not make for
// you, so a module cannot forget them:
//
//   1. Nothing renders until the config has loaded. `useGlobalConfig()` is
//      `undefined` on the first render, and formatAuditTimestamp's fallback
//      (`dd-MMM-yyyy HH:mm:ss`) would print a calendar date as
//      `03-Sep-2025 00:00:00`.
//   2. A tenant with no `date_format` row gets `dd-MMM-yyyy` (Aaron,
//      2026-09-27: "follow the config: 21-Apr-2026") — a DATE pattern, not the
//      audit fallback with its time.
//   3. `YYYY-MM-DD` is a LOCAL calendar day. formatAuditTimestamp reads it with
//      parseISO; a grid's `type: "date"` column needs a Date, and
//      `tenantDateValue` makes the same local-day read (never
//      `new Date("2026-09-28")`, which is UTC midnight and shows the 27th in
//      Alberta).
//
// Where the pattern comes from: `global_date_format = '%d-%b-%Y'` on mustang
// and g2, served as `date_format`, converted to date-fns (`dd-MMM-yyyy`) by
// `GlobalConfig.query` on load (src/config/GlobalConfig.ts).
//
// Display only. Never feed the result back into a request, a sort key, a
// grouping key or a DatePicker value — those stay ISO.
import { isValid, parseISO } from "date-fns";

import { useGlobalConfig } from "../hooks/useGlobalConfig";
import { formatAuditTimestamp } from "./formatAuditTimestamp";

/** The pattern a tenant with no `date_format` row gets: `21-Apr-2026`. */
export const DEFAULT_TENANT_DATE_FORMAT = "dd-MMM-yyyy";

type DateValue = string | Date | null | undefined;

/** The tenant's date pattern from a loaded global config (date-fns tokens),
 *  or `undefined` while the config has not loaded. */
export function tenantDatePattern(
  globalConfig: { [key: string]: any } | undefined
): string | undefined {
  if (!globalConfig) return undefined;
  const raw = globalConfig.date_format;
  return typeof raw === "string" && raw.trim() ? raw : DEFAULT_TENANT_DATE_FORMAT;
}

/**
 * The tenant's date-fns date pattern, or `undefined` until the config has
 * loaded. A string, so it is a stable `useMemo` dependency for grid columns.
 */
export function useTenantDateFormat(): string | undefined {
  return tenantDatePattern(useGlobalConfig());
}

/** A value formatAuditTimestamp could not read is shown as it came (as F&D's
 *  fdDate does): a raw date is better than a silently missing one. Empty and
 *  the MySQL zero-date stay "". */
function orRaw(out: string, value: DateValue): string {
  if (out !== "" || typeof value !== "string") return out;
  const v = value.trim();
  return v && !v.startsWith("0000-00-00") ? v : "";
}

/** A calendar date in the tenant's format. "" while `pattern` is undefined
 *  (config loading) or the value is empty. */
export function formatTenantDate(value: DateValue, pattern: string | undefined): string {
  if (!pattern) return "";
  return orRaw(formatAuditTimestamp(value, pattern), value);
}

/** A date-time: the date in the tenant's format, then `HH:mm`. */
export function formatTenantDateTime(value: DateValue, pattern: string | undefined): string {
  if (!pattern) return "";
  return orRaw(formatAuditTimestamp(value, `${pattern} HH:mm`), value);
}

/**
 * The value a grid's `type: "date"` column holds (it sorts and filters on a
 * Date): the wire string read as a LOCAL day, or null for empty, the MySQL
 * zero-date, and anything unreadable. Pair it with a `valueFormatter` that
 * calls `formatTenantDate` — the column's default formatter is the browser
 * locale's `toLocaleDateString()`.
 */
export function tenantDateValue(value: DateValue): Date | null {
  if (value == null || value === "") return null;
  if (value instanceof Date) return isValid(value) ? value : null;
  if (value.trim().startsWith("0000-00-00")) return null;
  const d = parseISO(value.trim());
  return isValid(d) ? d : null;
}
